import React, { useState, useEffect } from "react";
import { Button, Stack, Tooltip, Typography, Box } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import ConfirmDialog from "../../utils/ConfirmDialog";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { getSetting } from "../../services/settingServices";

export default function AgreementActions({
  agreement,
  onShow,
  onEdit,
  onReport,
  onEditIndicators,
  onUpdateStatus,
}) {
  const { status } = agreement;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [rectorEmail, setRectorEmail] = useState(null);


  useEffect(() => {
    async function fetchSetting() {
      try {
        const setting = await getSetting();
        setRectorEmail(setting?.rector_email || null);
      } catch (error) {
        console.error("Error al cargar settings:", error);
      }
    }
    fetchSetting();
  }, []);

  const handleOpenConfirm = (id, newStatus, label) => {
    setPendingAction({ id, newStatus, label });
    setConfirmOpen(true);
  };

  const handleClose = () => {
    setConfirmOpen(false);
    setPendingAction(null);
  };

  const handleConfirm = () => {
    if (pendingAction) {
      onUpdateStatus(pendingAction.id, pendingAction.newStatus);
    }
    handleClose();
  };

  const getRecipientEmail = (status, agreement) => {
    switch (status) {
      case "sent_to_rector":
        return rectorEmail || "Sin correo definido";
      case "sent_to_applicant":
        return agreement.proponent_email || "Sin correo definido";
      default:
        return null; // Para casos donde no hay envío de correo
    }
  };

  const confirmMessages = {
    sent_to_rector: "enviar este convenio al rector",
    rejected_by_rector: "rechazar este convenio como rector",
    approved_by_rector: "aprobar este convenio como rector",
    sent_to_applicant: "enviar este convenio al solicitante",
    rejected_by_applicant: "rechazar este convenio como solicitante",
    approved_by_applicant: "aprobar este convenio como solicitante",
    wait_to_sign: "marcar este convenio como pendiente de firma",
    signed: "marcar este convenio como firmado",
    finished: "marcar este convenio como finalizado",

  };

  // Cambia tu ConfirmDialog para que use esta función:
  const getConfirmTitle = () => {
    if (!pendingAction) return "Confirmar acción";

    const actionTitles = {
      rejected_by_applicant: `Confirmar: Rechazar convenio N° ${agreement.order_number} (como solicitante)`,
      rejected_by_rector: `Confirmar: Rechazar convenio N° ${agreement.order_number} (como rector)`,
      sent_to_rector: `Confirmar: Enviar convenio N° ${agreement.order_number} al rector`,
      sent_to_applicant: `Confirmar: Enviar convenio N° ${agreement.order_number} al solicitante`,
      approved_by_rector: `Confirmar: Aprobar convenio N° ${agreement.order_number} (como rector)`,
      approved_by_applicant: `Confirmar: Aprobar convenio N° ${agreement.order_number} (como solicitante)`,
      wait_to_sign: `Confirmar: Marcar convenio N° ${agreement.order_number} como pendiente de firma`,
      signed: `Confirmar: Marcar convenio N° ${agreement.order_number} como firmado`,
      finished: `Confirmar: Marcar convenio N° ${agreement.order_number} como finalizado`
    };

    return actionTitles[pendingAction.newStatus] || `Confirmar: ${pendingAction.label} (Convenio N° ${agreement.order_number})`;
  };


  const renderConfirmMessage = () => {
    if (!pendingAction) {
      return "¿Estás seguro de que deseas realizar esta acción?";
    }

    const baseMessage =
      confirmMessages[pendingAction.newStatus] || "realizar esta acción";
    const recipient = getRecipientEmail(pendingAction.newStatus, agreement);

    return (
      <Box>
        <Box display="flex" alignItems="center" mb={2}>
          <WarningAmberIcon color="warning" sx={{ mr: 1 }} />
          <Typography variant="body1">
            Estás a punto de <strong>{baseMessage}</strong>. Una vez confirmada, no podrás deshacerla.
          </Typography>
        </Box>

        <Box
          mt={1}
          p={2}
          borderRadius={1}
          bgcolor="#f5f5f5"
          sx={{ border: "1px solid #ddd" }}
        >
          <Typography><strong>📄 N° de orden:</strong> {agreement.order_number}</Typography>
          <Typography><strong>🎯 Objetivo:</strong> {agreement.objective}</Typography>
        </Box>

        {recipient && (
          <Box
            mt={2}
            p={1}
            borderRadius={1}
            bgcolor={recipient === "Sin correo definido" ? "#fff3cd" : "#e3f2fd"}
            display="flex"
            alignItems="center"
          >
            <EmailIcon
              color={recipient === "Sin correo definido" ? "warning" : "primary"}
              fontSize="small"
            />
            <Typography ml={1}>{recipient}</Typography>
          </Box>
        )}

        {recipient === "Sin correo definido" && (
          <Typography variant="caption" color="error">
            ⚠ No se enviará ningún correo electrónico porque no hay dirección registrada.
          </Typography>
        )}
      </Box>
    );
  };
  

  return (
    <>
      <Stack direction="row" spacing={1} justifyContent="center">
        <Tooltip title="Ver convenio">
          <Button
            onClick={() => onShow(agreement.id)}
            size="small"
            variant="outlined"
          >
            Ver
          </Button>
        </Tooltip>
        {status === "draft" && (
          <>
            <Tooltip title="Editar convenio">
              <Button
                onClick={() => onEdit(agreement.id)}
                size="small"
                variant="outlined"
              >
                Editar
              </Button>
            </Tooltip>
            <Tooltip title="Analizar el convenio">
              <Button
                onClick={() => onReport(agreement.id, agreement.agreement_type)}
                size="small"
                variant="contained"
                color="primary"
              >
                Analizar
              </Button>
            </Tooltip>
          </>
        )}

        {status === "process" && (
          <>
            <Tooltip title="Editar convenio">
              <Button
                onClick={() => onEdit(agreement.id)}
                size="small"
                variant="outlined"
              >
                Editar
              </Button>
            </Tooltip>
            <Tooltip title="Enviar convenio al rector">
              <Button
                onClick={() =>
                  handleOpenConfirm(
                    agreement.id,
                    "sent_to_rector",
                    "enviar convenio al rector"
                  )
                }
                size="small"
                variant="contained"
                color="success"
              >
                Enviar
              </Button>
            </Tooltip>
          </>
        )}

        {status === "sent_to_rector" && (
          <>
            <Tooltip title="Rechazar convenio">
              <Button
                onClick={() =>
                  handleOpenConfirm(
                    agreement.id,
                    "rejected_by_rector",
                    "marcar como rechazado por rector"
                  )
                }
                size="small"
                variant="outlined"
                color="error"
              >
                Rechazar
              </Button>
            </Tooltip>
            <Tooltip title="Aceptar convenio">
              <Button
                onClick={() =>
                  handleOpenConfirm(
                    agreement.id,
                    "approved_by_rector",
                    "marcar como aceptado por rector"
                  )
                }
                size="small"
                variant="outlined"
                color="success"
              >
                Aceptar
              </Button>
            </Tooltip>
          </>
        )}

        {status === "approved_by_rector" && (
          <>
            <Button
                onClick={() => onEdit(agreement.id)}
                size="small"
                variant="outlined"
              >
                Editar
              </Button>
            <Tooltip title="Enviar convenio al solicitante">
              <Button
                onClick={() =>
                  handleOpenConfirm(
                    agreement.id,
                    "sent_to_applicant",
                    "enviar convenio al solicitante"
                  )
                }
                size="small"
                variant="contained"
                color="success"
              >
                Enviar
              </Button>
            </Tooltip>
          </>
        )}

        {status === "sent_to_applicant" && (
          <>
            <Tooltip title="Rechazar convenio">
              <Button
                onClick={() =>
                  handleOpenConfirm(
                    agreement.id,
                    "rejected_by_applicant",
                    "marcar como rechazado por solicitante"
                  )
                }
                size="small"
                variant="outlined"
                color="error"
              >
                Rechazar
              </Button>
            </Tooltip>
            <Tooltip title="Aceptar convenio">
              <Button
                onClick={() =>
                  handleOpenConfirm(
                    agreement.id,
                    "approved_by_applicant",
                    "marcar como aceptado por solicitante"
                  )
                }
                size="small"
                variant="outlined"
                color="success"
              >
                Aceptar
              </Button>
            </Tooltip>
          </>
        )}

        {status === "approved_by_applicant" && (
          <>
            <Button
                onClick={() => onEdit(agreement.id)}
                size="small"
                variant="outlined"
              >
                Editar
            </Button>
            <Tooltip title="Marcar como pendiente de firma">
              <Button
                onClick={() =>
                  handleOpenConfirm(
                    agreement.id,
                    "wait_to_sign",
                    "marcar como pendiente de firma"
                  )
                }
                size="small"
                variant="contained"
                color="success"
                sx={{ width: 120 , whiteSpace: "nowrap" }}
              >
                Esperar firmar
              </Button>
            </Tooltip>
          </>
        )}

        {status === "wait_to_sign" && (
          <>
            <Button
                onClick={() => onEdit(agreement.id)}
                size="small"
                variant="outlined"
              >
                Editar
            </Button>
            <Tooltip title="Marcar como firmado">
              <Button
                onClick={() =>
                  handleOpenConfirm(
                    agreement.id,
                    "signed",
                    "marcar como firmado"
                  )
                }
                size="small"
                variant="contained"
                color="success"
              >
                Firmado
              </Button>
            </Tooltip>
          </>
        )}

        {(status === "signed" || status === "next_to_expired") && (
          <>
            {agreement.agreement_type === "especifico" && (
              <Tooltip title="Marcar indicadores">
                <Button
                  onClick={() => onEditIndicators(agreement.id)}
                  size="small"
                >
                  Indicadores
                </Button>
              </Tooltip>
            )}
            <Button
                onClick={() => onEdit(agreement.id)}
                size="small"
                variant="outlined"
              >
                Editar
            </Button>
            <Tooltip title="Marcar como terminado">
              <Button
                onClick={() =>
                  handleOpenConfirm(
                    agreement.id,
                    "finished",
                    "marcar como terminado"
                  )
                }
                size="small"
                variant="contained"
                color="success"
              >
                Terminar
              </Button>
            </Tooltip>
          </>
        )}
      </Stack>
      <ConfirmDialog
        open={confirmOpen}
        handleClose={handleClose}
        onConfirm={handleConfirm}
        title={getConfirmTitle()}
        message={renderConfirmMessage()}
        confirmText={
          pendingAction?.newStatus?.startsWith("rejected")
            ? "Sí, rechazar"
            : `Sí, ${pendingAction?.label || "confirmar"}`
        }
        confirmColor={pendingAction?.newStatus?.startsWith("rejected") ? "error" : "primary"}
      />
    </>
  );
}
